import { NavLink, Outlet, useNavigate } from 'react-router'
import { Building2, CreditCard, LogOut, Receipt, ScrollText, ShieldCheck, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePlatformAuthStore } from '../store'

/**
 * The frame around the operator console.
 *
 * <p>Visibly not the product. A different chrome, a slate top bar rather than the tenant
 * purple, and the installation's name where a customer's logo would be — so that nobody
 * doing support at the end of a long day mistakes a page listing every customer for the
 * page of the customer they were just looking at.
 */
export function PlatformShell() {
  const navigate = useNavigate()
  const operator = usePlatformAuthStore((state) => state.operator)
  const logout = usePlatformAuthStore((state) => state.logout)

  function signOut() {
    logout()
    void navigate('/platform/login', { replace: true })
  }

  const links = [
    { to: '/platform/tenants', label: 'Workspaces', icon: Building2 },
    { to: '/platform/plans', label: 'Plans', icon: CreditCard },
    { to: '/platform/billing', label: 'Billing', icon: Receipt },
    { to: '/platform/operators', label: 'Operators', icon: Users },
    { to: '/platform/audit', label: 'Audit', icon: ScrollText },
  ]

  return (
    <div className="min-h-dvh bg-[var(--color-page)]">
      <header className="bg-slate-900 text-white">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3">
          <div className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="size-5" aria-hidden />
            <span>HUMIS operations</span>
          </div>

          <nav className="flex items-center gap-1" aria-label="Operator sections">
            {links.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-white/15 font-medium' : 'text-white/75 hover:bg-white/10',
                  ].join(' ')
                }
              >
                <Icon className="size-4" aria-hidden />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ms-auto flex items-center gap-3 text-sm">
            <span className="text-white/75">
              {operator?.fullName} · {operator?.role.replace('_', ' ').toLowerCase()}
            </span>
            <Button variant="ghost" size="sm" onClick={signOut} className="text-white hover:bg-white/10">
              <LogOut className="me-2 size-4" aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
